import React, {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { translateColumnOverflowHintMessage } from '../domain/labels';
import { EMAIL_BUILDER_DOM_ID_PREFIX, useStableDomId } from '../useStableDomId';
import { Layers, Lock } from '../emailBuilderIcons';
import { ActionRail } from './ActionRail';
import { MultiSelectActionRail } from './MultiSelectActionRail';
import { RowMultiSelectActionRail } from './RowMultiSelectActionRail';
import { FormattingRail } from './FormattingRail';
import { SelectionEdgeStrips } from './SelectionEdgeStrips';
import type { SelectionEdge } from './emailBuilderSelectionEdgeStrips';
import {
  resolveGroupedRowSeamBleedPx,
  resolveRowSelectionEdgeStripPx,
} from './emailBuilderSelectionEdgeStrips';
import { TouchRailBar } from './TouchRailBar';
import { TouchFormattingRail } from './TouchFormattingRail';
import { useEmailBuilderRailViewport } from './useEmailBuilderRailViewport';
import {
  renderEmailBuilderCanvasRailPortal,
  useEmailBuilderCanvasRailPortalHost,
} from './useEmailBuilderCanvasRailPortal';
import { useEditorDrag } from './dnd/useEditorDrag';
import { canDragCanvasItem } from './dnd/canvas-drag-eligibility';
import {
  isCanvasDragActive,
  isColumnResizeHandleTarget,
  isImageResizeHandleTarget,
} from './dnd/pointer-guard';
import { getEditorStoreState } from '../domain/store';
import { useRegisterHoverTarget } from './dnd/drop-slot-registry';
import {
  useCanvasBlockMultiSelectChrome,
  useCanvasFrameLabelStack,
  useCanvasFrameSuppressActivation,
  useCanvasRowMultiSelectChrome,
} from './canvasFrameChromeContext';
import type { DragData } from '../domain/dnd/types';
import type { Selection, TextVariant } from '../domain/types';
import type { TEmailBuilderCanvasBlockFormattingRailTextVariant } from '../editing/emailBuilderCanvasBlockInteractions';

export type FrameLevel = 'column' | 'row' | 'block' | 'group';

export type AncestorChip = {
  label: string;
  level: FrameLevel;
  onSelect: (event: ReactMouseEvent) => void;
  onHover: () => void;
};

type FrameHoverTarget =
  | {
      kind: 'block';
      rowId: string;
      columnId: string;
      blockId: string;
      index: number;
    }
  | {
      kind: 'row';
      rowId: string;
    }
  | {
      kind: 'group';
      groupId: string;
      sectionIndex: number;
    }
  | {
      kind: 'column';
      rowId: string;
      columnId: string;
    };

type SelectableFrameProps = {
  level: FrameLevel;
  entityId: string;
  label: string;
  selected: boolean;
  subselected?: boolean;
  hovered: boolean;
  showLabel: boolean;
  locked?: boolean;
  lockTitle?: string;
  empty?: boolean;
  dropTarget?: boolean;
  drag?: { id: string; data: DragData };
  onSelect: (event: ReactMouseEvent) => boolean | void;
  onHover: (event: ReactMouseEvent) => void;
  ancestors?: AncestorChip[];
  frameSelection?: Selection;
  insideGroup?: boolean;
  groupRowIndex?: number;
  groupRowLast?: boolean;
  descendantHovered?: boolean;
  textEditing?: boolean;
  textVariant?: TextVariant | 'button';
  inlineTextBlockId?: string;
  hoverTarget?: FrameHoverTarget;
  boxStyle?: CSSProperties;
  layoutOverflow?: boolean;
  selectionEdgeSuppressedEdges?: readonly SelectionEdge[];
  selectionEdgeForcedHighlight?: readonly SelectionEdge[];
  children: ReactNode;
};

const PLACEHOLDER_DRAG: DragData = { kind: 'palette-block', blockType: 'text' };

function isTextInteractionTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      '.ProseMirror, [contenteditable="true"], [data-text-toolbar], [data-text-toolbar-mount]',
    ) !== null
  );
}

function isEditorChromeControlTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      '[data-editor-chrome] button, [data-text-toolbar] button',
    ) !== null
  );
}

export function SelectableFrame({
  level,
  entityId,
  label,
  selected,
  subselected = false,
  hovered,
  showLabel,
  locked = false,
  lockTitle,
  empty = false,
  dropTarget = false,
  drag,
  onSelect,
  onHover,
  ancestors = [],
  frameSelection,
  insideGroup = false,
  groupRowIndex,
  groupRowLast = false,
  descendantHovered = false,
  textEditing = false,
  textVariant,
  inlineTextBlockId,
  hoverTarget,
  boxStyle,
  layoutOverflow = false,
  selectionEdgeSuppressedEdges = [],
  selectionEdgeForcedHighlight = [],
  children,
}: SelectableFrameProps) {
  const { t } = useTranslation();
  const suppressActivation = useCanvasFrameSuppressActivation();
  const {
    stackAnchorGhost,
    labelPreviewLevel,
    labelStackPinned,
    onOpenLabelStack,
    onCloseLabelStack,
    onPreviewHover,
    onClearAncestorPreview,
  } = useCanvasFrameLabelStack(frameSelection);
  const blockId = level === 'block' ? entityId.replace(/^block:/, '') : null;
  const rowId = level === 'row' ? entityId.replace(/^row:/, '') : null;
  const {
    multiSelectActive,
    multiSelectRailHost,
    multiSelectPrimary,
  } = useCanvasBlockMultiSelectChrome(blockId);
  const {
    rowMultiSelectActive,
    rowMultiSelectMode,
    rowMultiSelectRailHost,
    rowMultiSelectPrimary,
  } = useCanvasRowMultiSelectChrome(rowId);
  const fallbackId = useStableDomId(EMAIL_BUILDER_DOM_ID_PREFIX);
  const lockDescriptionId = useStableDomId('lock-description');
  const isRowOrGroupDrag = Boolean(drag) && (level === 'row' || level === 'group');
  const frameDraggable =
    drag?.data !== undefined &&
    canDragCanvasItem(getEditorStoreState(), drag.data);
  const skipNextClickRef = useRef(false);
  const { dragHandleRef } = useEditorDrag(
    drag?.data ?? PLACEHOLDER_DRAG,
    {
      id: drag?.id ?? fallbackId,
      rowGroupDrag: isRowOrGroupDrag,
      dragLevel: level === 'group' ? 'group' : 'row',
      // Collecting isDragging re-renders this frame during dragstart; Chrome
      // aborts the native drag if the source mutates in that window.
      collectDraggingState: false,
    },
  );

  const showAsSelected = selected;
  const displayLabel = showLabel;

  const frameRef = useRef<HTMLDivElement | null>(null);
  const dragSourceId = drag?.id;
  const setFrameNode = useCallback(
    (node: HTMLDivElement | null) => {
      frameRef.current = node;
      // Keep this callback identity stable across isDragging / parent re-renders.
      // An inline ref disconnects react-dnd (draggable=false) mid-dragstart
      // and Chrome aborts the native drag (~30ms) — worse for img/button frames.
      dragHandleRef(dragSourceId ? node : null);
    },
    [dragSourceId, dragHandleRef],
  );
  const getFrameRect = useCallback(
    () => frameRef.current?.getBoundingClientRect(),
    [],
  );

  useRegisterHoverTarget(
    hoverTarget?.kind === 'block'
      ? {
          id: `block-hover:${hoverTarget.rowId}:${hoverTarget.columnId}:${hoverTarget.blockId}`,
          kind: 'block',
          rowId: hoverTarget.rowId,
          columnId: hoverTarget.columnId,
          blockId: hoverTarget.blockId,
          index: hoverTarget.index,
          getRect: getFrameRect,
        }
      : hoverTarget?.kind === 'row'
        ? {
            id: `row-hover:${hoverTarget.rowId}`,
            kind: 'row',
            rowId: hoverTarget.rowId,
            getRect: getFrameRect,
          }
        : hoverTarget?.kind === 'group'
          ? {
              id: `group-hover:${hoverTarget.groupId}`,
              kind: 'group',
              groupId: hoverTarget.groupId,
              sectionIndex: hoverTarget.sectionIndex,
              getRect: getFrameRect,
            }
          : hoverTarget?.kind === 'column'
            ? {
                id: `column-hover:${hoverTarget.rowId}:${hoverTarget.columnId}`,
                kind: 'column',
                rowId: hoverTarget.rowId,
                columnId: hoverTarget.columnId,
                getRect: getFrameRect,
              }
            : null,
  );

  const { usesHorizontalCompactTouchRail, usesSplitFormattingRail, isMobileLayout } =
    useEmailBuilderRailViewport();
  const showTouchRail =
    showAsSelected &&
    usesHorizontalCompactTouchRail &&
    (!multiSelectActive || multiSelectRailHost) &&
    (!rowMultiSelectActive || rowMultiSelectRailHost);
  const showTouchFormattingRail =
    showTouchRail &&
    textEditing &&
    !multiSelectActive &&
    level === 'block' &&
    textVariant !== undefined;
  const showVerticalActionRail =
    showAsSelected &&
    !usesHorizontalCompactTouchRail &&
    !multiSelectActive &&
    !rowMultiSelectActive;
  const showMultiSelectActionRail =
    showAsSelected &&
    !usesHorizontalCompactTouchRail &&
    multiSelectActive &&
    multiSelectRailHost;
  const showRowMultiSelectActionRail =
    showAsSelected &&
    !usesHorizontalCompactTouchRail &&
    rowMultiSelectActive &&
    rowMultiSelectRailHost;
  const showCanvasPortaledRail =
    showVerticalActionRail ||
    showMultiSelectActionRail ||
    showRowMultiSelectActionRail ||
    showTouchRail ||
    showTouchFormattingRail;
  const showSelectionEdgeStrips =
    !suppressActivation &&
    !textEditing &&
    (level === 'row' || (level === 'column' && !empty));
  const railPortalHost = useEmailBuilderCanvasRailPortalHost(
    showCanvasPortaledRail,
    frameRef,
  );
  const useRowGroupActionRailAnchor = level === 'row' || level === 'group';

  useEffect(() => {
    if (level !== 'row') {
      return;
    }
    const frame = frameRef.current;
    if (!frame) {
      return;
    }

    const syncRowEdgeStripMetrics = () => {
      const height = frame.getBoundingClientRect().height;
      const stripPx = resolveRowSelectionEdgeStripPx(height);
      const seamBleed = resolveGroupedRowSeamBleedPx(height);
      frame.style.setProperty('--selection-edge-strip-size', `${stripPx}px`);
      frame.style.setProperty('--grouped-row-seam-bleed', `${seamBleed}px`);
    };

    syncRowEdgeStripMetrics();
    const observer = new ResizeObserver(syncRowEdgeStripMetrics);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [level]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) {
      return;
    }

    const isDirectHitFrame = (
      event: globalThis.PointerEvent | globalThis.MouseEvent,
    ) => {
      if (!(event.target instanceof Element)) {
        return false;
      }
      return event.target.closest('.email-builder-canvas-frame') === frame;
    };

    const shouldActivateFrame = (
      event: globalThis.PointerEvent | globalThis.MouseEvent,
    ) => {
      if (!isDirectHitFrame(event)) {
        return false;
      }
      if (
        isTextInteractionTarget(event.target) ||
        isEditorChromeControlTarget(event.target)
      ) {
        return false;
      }
      if (
        event.target instanceof Element &&
        event.target.closest('[data-slot-index]')
      ) {
        return false;
      }
      if (
        isColumnResizeHandleTarget(event.target) ||
        isImageResizeHandleTarget(event.target)
      ) {
        return false;
      }
      return true;
    };

    const onPointerDown = (event: globalThis.PointerEvent) => {
      if (event.button !== 0 || !shouldActivateFrame(event)) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }
      if (selected) {
        return;
      }
      const activated = onSelect(
        event as unknown as ReactMouseEvent<HTMLDivElement>,
      );
      if (activated !== false) {
        skipNextClickRef.current = true;
      }
    };

    const onClick = (event: globalThis.MouseEvent) => {
      if (!shouldActivateFrame(event)) {
        return;
      }
      if (
        skipNextClickRef.current &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey
      ) {
        skipNextClickRef.current = false;
        return;
      }
      onSelect(event as unknown as ReactMouseEvent<HTMLDivElement>);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (suppressActivation || isTextInteractionTarget(event.target)) {
        return;
      }
      if (event.key === ' ') {
        event.preventDefault();
        onSelect(event as unknown as ReactMouseEvent<HTMLDivElement>);
        return;
      }
      if (event.key === 'Enter' && !selected) {
        event.preventDefault();
        onSelect(event as unknown as ReactMouseEvent<HTMLDivElement>);
      }
    };

    const onMouseOver = (event: globalThis.MouseEvent) => {
      if (suppressActivation || isCanvasDragActive()) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-editor-chrome]')
      ) {
        return;
      }
      onHover(event as unknown as ReactMouseEvent<HTMLDivElement>);
    };

    const onMouseMove = (event: globalThis.MouseEvent) => {
      if (suppressActivation || isCanvasDragActive()) {
        return;
      }
      if (level !== 'block' && level !== 'row') {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[data-editor-chrome]')
      ) {
        return;
      }
      onHover(event as unknown as ReactMouseEvent<HTMLDivElement>);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (suppressActivation || isCanvasDragActive()) {
        return;
      }
      onHover(event as unknown as ReactMouseEvent<HTMLDivElement>);
    };

    frame.addEventListener('pointerdown', onPointerDown);
    frame.addEventListener('click', onClick);
    frame.addEventListener('keydown', onKeyDown);
    frame.addEventListener('mouseover', onMouseOver);
    frame.addEventListener('mousemove', onMouseMove);
    frame.addEventListener('focusin', onFocusIn);

    return () => {
      frame.removeEventListener('pointerdown', onPointerDown);
      frame.removeEventListener('click', onClick);
      frame.removeEventListener('keydown', onKeyDown);
      frame.removeEventListener('mouseover', onMouseOver);
      frame.removeEventListener('mousemove', onMouseMove);
      frame.removeEventListener('focusin', onFocusIn);
    };
  }, [level, onHover, onSelect, selected, suppressActivation]);

  const claimChromeHover = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onHover(event);
  };

  const claimSelectionHover = (event: ReactMouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onHover(event as ReactMouseEvent<HTMLDivElement>);
  };

  const handleLabelHitMouseOver = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (
      labelStackPinned &&
      event.target instanceof Element &&
      event.target.closest('button[data-level]')
    ) {
      return;
    }
    if (labelStackPinned) {
      onPreviewHover?.();
      return;
    }
    onClearAncestorPreview?.();
    claimChromeHover(event);
  };

  const handleCurrentLabelMouseEnter = (
    event: ReactMouseEvent<HTMLSpanElement>,
  ) => {
    if (!labelStackPinned) {
      return;
    }
    event.stopPropagation();
    onPreviewHover?.();
  };

  const toggleLabelStack = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (labelStackPinned) {
      onCloseLabelStack?.();
      return;
    }
    if (!selected) {
      onSelect(event as unknown as ReactMouseEvent<HTMLDivElement>);
    }
    onOpenLabelStack?.();
  };

  const chipPreviewActive = (chipLevel: FrameLevel) =>
    labelStackPinned && labelPreviewLevel === chipLevel;

  const labelChrome = displayLabel ? (
    <div
      className={"email-builder-canvas-frame__label-hit"}
      data-editor-chrome
      data-frame-label
      onMouseOver={handleLabelHitMouseOver}
      onFocus={event => event.stopPropagation()}>
      {labelStackPinned
        ? ancestors.map(item => (
            <button
              key={`${item.level}:${item.label}`}
              type="button"
              className={"email-builder-canvas-frame__chip"}
              data-level={item.level}
              data-active={chipPreviewActive(item.level) ? 'true' : 'false'}
              onMouseDown={event => event.stopPropagation()}
              onMouseEnter={event => {
                event.stopPropagation();
                item.onHover();
              }}
              onClick={event => {
                event.stopPropagation();
                item.onSelect(event);
              }}>
              {item.label}
            </button>
          ))
        : null}
      <span
        className={"email-builder-canvas-frame__label"}
        data-level={level}
        data-active={chipPreviewActive(level) ? 'true' : 'false'}
        onMouseEnter={handleCurrentLabelMouseEnter}
        title={
          layoutOverflow && level === 'block'
            ? translateColumnOverflowHintMessage(t)
            : locked
              ? lockTitle
              : drag && !isMobileLayout
                ? t('Drag to move')
                : undefined
        }>
        {locked ? (
          <Lock
            size={11}
            aria-hidden
            style={{
              display: 'inline',
              marginRight: 4,
              verticalAlign: '-1px',
            }}
          />
        ) : null}
        {label}
        {layoutOverflow && level === 'block' ? (
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className={"email-builder-canvas-frame__label-overflow-icon"}
            aria-hidden
          />
        ) : null}
      </span>
      {ancestors.length > 0 ? (
        <button
          type="button"
          className={"email-builder-canvas-frame__expand"}
          aria-label={t('Parent layers')}
          onMouseDown={event => event.stopPropagation()}
          onClick={toggleLabelStack}>
          <Layers size={12} aria-hidden />
        </button>
      ) : null}
    </div>
  ) : null;

  const frameBody = (
    <div className={"email-builder-canvas-frame__content"} data-frame-content>
      {children}
    </div>
  );

  return (
    <div
      ref={setFrameNode}
      className={"email-builder-canvas-frame"}
      data-entity-id={entityId}
      data-level={level}
      data-inside-group={insideGroup ? 'true' : 'false'}
      data-group-row-index={
        insideGroup && groupRowIndex !== undefined
          ? String(groupRowIndex)
          : undefined
      }
      data-group-row-last={insideGroup && groupRowLast ? 'true' : undefined}
      data-descendant-hovered={descendantHovered ? 'true' : 'false'}
      data-draggable={frameDraggable ? 'true' : 'false'}
      data-selected={showAsSelected ? 'true' : 'false'}
      data-subselected={subselected ? 'true' : 'false'}
      data-multi-select={multiSelectActive ? 'true' : undefined}
      data-multi-select-primary={multiSelectPrimary ? 'true' : undefined}
      data-row-multi-select={rowMultiSelectActive ? 'true' : undefined}
      data-row-multi-select-mode={rowMultiSelectMode ? 'true' : undefined}
      data-row-multi-select-rail-host={rowMultiSelectRailHost ? 'true' : undefined}
      data-hovered={!showAsSelected && hovered ? 'true' : 'false'}
      data-stack-anchor={stackAnchorGhost ? 'true' : 'false'}
      data-empty={empty ? 'true' : 'false'}
      data-drop-target={dropTarget ? 'true' : 'false'}
      data-text-editing={textEditing ? 'true' : 'false'}
      style={{
        ...boxStyle,
      }}
      role={
        ancestors.length === 0 && !suppressActivation ? 'button' : undefined
      }
      aria-label={label}
      aria-describedby={locked && lockTitle ? lockDescriptionId : undefined}
      tabIndex={
        showAsSelected &&
        !textEditing &&
        !suppressActivation &&
        (!multiSelectActive || multiSelectPrimary) &&
        (!rowMultiSelectActive || rowMultiSelectPrimary)
          ? 0
          : -1
      }>
      {locked && lockTitle ? (
        <span id={lockDescriptionId} className="sr-only">
          {lockTitle}
        </span>
      ) : null}
      {labelChrome}
      {frameBody}
      {showSelectionEdgeStrips ? (
        <SelectionEdgeStrips
          level={level}
          suppressedEdges={selectionEdgeSuppressedEdges}
          forcedHighlightEdges={selectionEdgeForcedHighlight}
        />
      ) : null}
      {showVerticalActionRail
        ? renderEmailBuilderCanvasRailPortal(
            <ActionRail
              frameRef={frameRef}
              useRowGroupAnchor={useRowGroupActionRailAnchor}
              onClaimHover={claimSelectionHover}
            />,
            railPortalHost,
          )
        : null}
      {showMultiSelectActionRail
        ? renderEmailBuilderCanvasRailPortal(
            <MultiSelectActionRail
              frameRef={frameRef}
              onClaimHover={claimSelectionHover}
            />,
            railPortalHost,
          )
        : null}
      {showRowMultiSelectActionRail
        ? renderEmailBuilderCanvasRailPortal(
            <RowMultiSelectActionRail
              frameRef={frameRef}
              onClaimHover={claimSelectionHover}
            />,
            railPortalHost,
          )
        : null}
      {showTouchRail
        ? renderEmailBuilderCanvasRailPortal(
            <TouchRailBar
              frameRef={frameRef}
              multiSelect={multiSelectActive}
            />,
            railPortalHost,
          )
        : null}
      {showTouchFormattingRail
        ? renderEmailBuilderCanvasRailPortal(
            <TouchFormattingRail
              frameRef={frameRef}
              blockId={inlineTextBlockId ?? entityId.replace(/^block:/, '')}
              typeLabel={label}
              textVariant={textVariant as TEmailBuilderCanvasBlockFormattingRailTextVariant}
            />,
            railPortalHost,
          )
        : null}
      {textEditing && usesSplitFormattingRail && !multiSelectActive ? (
        <div
          className={"email-builder-canvas-frame__toolbar-chrome"}
          data-text-toolbar-mount
          data-editor-chrome
          onMouseOver={claimChromeHover}
          onFocus={event => event.stopPropagation()}>
          {level === 'block' && textVariant !== undefined ? (
            <FormattingRail
              blockId={inlineTextBlockId ?? entityId.replace(/^block:/, '')}
              typeLabel={label}
              textVariant={textVariant as TEmailBuilderCanvasBlockFormattingRailTextVariant}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
